import React, { useState, useMemo } from "react";
import { Box, Paper, Typography, IconButton, Collapse, Divider, Button, Tooltip } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

/**
 * Helper to compute a cell's value similar to DataGrid behavior.
 * - Supports valueGetter, valueFormatter when present.
 * - Attempts to call renderCell if provided (best-effort), falling back to text.
 */
function getDisplayForColumn(col, row) {
  // 1) raw value
  let value = row?.[col.field];

  // 2) valueGetter (if any)
  if (typeof col.valueGetter === "function") {
    try {
      // MUI passes a params object; we emulate minimally
      value = col.valueGetter({ row, id: row?.id, field: col.field, value });
    } catch {
      /* ignore valueGetter errors in card view */
    }
  }

  // 3) valueFormatter (if any)
  if (typeof col.valueFormatter === "function") {
    try {
      value = col.valueFormatter({ value, row, id: row?.id, field: col.field });
    } catch {
      /* ignore formatter errors in card view */
    }
  }

  // 4) renderCell (if any) – may return a React node
  if (typeof col.renderCell === "function") {
    try {
      const node = col.renderCell({ value, row, id: row?.id, field: col.field });
      if (node !== undefined && node !== null) return node;
    } catch {
      /* ignore renderCell errors in card view */
    }
  }

  return value ?? "";
}

/**
 * MobileCardList – rendered under small screens.
 * Props:
 * - rows: array of data rows
 * - columns: DataGrid columns (used to drive labels/values)
 * - tooltipFields: string[] -> fields that should show tooltip
 * - initiallyHiddenColumns: string[] -> fields hidden in collapsed preview
 * - onRowClick: function({ row, id, field }) -> same shape you used in DataGrid
 * - mobilePrimaryFields?: string[] optional to explicitly pick top-of-card fields
 */
export default function MobileCardList({
  rows,
  columns,
  tooltipFields = [],
  initiallyHiddenColumns = [],
  onRowClick,
  mobilePrimaryFields, // optional override
  pageSize = 50, // number of cards rendered initially; a "Load more" adds +pageSize
}) {
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  const [visibleCount, setVisibleCount] = useState(pageSize);

  const orderedColumns = useMemo(() => {
    // Keep original column order; filter out truly hidden columns (if any were set)
    return columns.filter(Boolean);
  }, [columns]);

  // Build a quick lookup by field for O(1) acces
  const columnByField = useMemo(() => {
    const map = new Map();
    for (const col of orderedColumns) {
      map.set(col.field, col);
    }
    return map;
  }, [orderedColumns]);

  // Primary fields: respect the order of mobilePrimaryFields if provided
  const primaryFields = useMemo(() => {
    if (Array.isArray(mobilePrimaryFields) && mobilePrimaryFields.length > 0) {
      const seen = new Set();
      return mobilePrimaryFields
        .map((field) => columnByField.get(field))
        .filter((col) => col && !seen.has(col.field) && (seen.add(col.field), true));
    }
    // Fallback: first 2 non-hidden columns (preserves orderedColumns order)
    return orderedColumns
      .filter((c) => !initiallyHiddenColumns.includes(c.field))
      .slice(0, 2);
  }, [mobilePrimaryFields, orderedColumns, initiallyHiddenColumns, columnByField]);

  // Secondary fields: everything else (preserving orderedColumns order)
  const secondaryFields = useMemo(() => {
    const primarySet = new Set(primaryFields.map((c) => c.field));
    return orderedColumns.filter((c) => !primarySet.has(c.field));
  }, [orderedColumns, primaryFields]);


  const handleToggleExpand = (rowId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(rowId) ? next.delete(rowId) : next.add(rowId);
      return next;
    });
  };

  const handleRowClick = (row) => {
    if (onRowClick) {
      onRowClick({ row, id: row.id, field: null });
    }
  };

  const renderField = (col, row) => {
    const label = col.headerName ?? col.field;
    const content = getDisplayForColumn(col, row);

    const body = (
      <Typography
        variant="body2"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
          // Let primary line stay single; secondary can wrap inside collapse
          whiteSpace: "nowrap",
        }}
      >
        {content}
      </Typography>
    );

    const withTooltip =
      tooltipFields.includes(col.field) ? (
        <Tooltip title={typeof content === "string" ? content : ""} arrow>
          <span>{body}</span>
        </Tooltip>
      ) : (
        body
      );

    return (
      <Box key={col.field} sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {label}
        </Typography>
        {withTooltip}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
      {rows.slice(0, visibleCount).map((row) => {
        const isExpanded = expandedRows.has(row.id);
        return (
          <Paper
            key={row.id}
            variant="outlined"
            sx={{
              p: 1.25,
              borderRadius: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
            }}
          >
            {/* Tap anywhere (except expand icon) to "select" row */}

{/* Tap anywhere (except expand icon) to "select" row */}
            <Box
              onClick={(e) => {
                  e.stopPropagation();
                  handleToggleExpand(row.id);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                cursor: onRowClick ? "pointer" : "default",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr", // first row: two columns
                  rowGap: 0.5,
                  minWidth: 0,
                  flex: 1,
                }}
              >
                {primaryFields.map((col, index) => (
                  <Box
                    key={col.key || index}
                    sx={{
                      // first two fields share the row
                      // remaining fields span both columns
                      gridColumn: index < 2 ? "auto" : "1 / -1",
                    }}
                  >
                    {renderField(col, row)}
                  </Box>
                ))}
              </Box>


              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleExpand(row.id);
                }}
                aria-label={isExpanded ? "Collapse details" : "Expand details"}
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}> 
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1,
                  }}
                >
                  {secondaryFields
                    .filter((c) => !initiallyHiddenColumns.includes(c.field))
                    .map((col) => (
                      <Box key={col.field} sx={{ minWidth: 0 }}>
                        {/* In expanded view allow wrapping */}
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {col.headerName ?? col.field}
                        </Typography>
                        <Box
                          sx={{
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                          }}
                        >
                          {getDisplayForColumn(col, row)}
                        </Box>
                      </Box>
                    ))}
                </Box>
                <Button onClick={() => handleRowClick(row)} variant="outlined" size="small" color="primary" sx={{}}>More Details</Button>
              </Box>
            </Collapse>
          </Paper>
        );
      })}

      {rows.length > visibleCount && (
        <Button
          variant="outlined"
          onClick={() => setVisibleCount((c) => c + pageSize)}
          sx={{ alignSelf: "center", mt: 1 }}
        >
          Load more
        </Button>
      )}
    </Box>
  );
}